/**
 * Which forms a child is expected to submit, on the frontend.
 *
 * THIS MIRRORS backend/services/formApplicability.js AND backend/services/formType.js.
 * It is duplicated rather than imported because CRA cannot import from outside src/ - the same
 * reason services/schoolBuilding.js has a frontend twin. If the rule changes, change it in all
 * of them, or the district is told a child owes a form the parent was never shown.
 *
 * WHY THIS FILE EXISTS AT ALL
 * The rule was open-coded at each call site as substring tests against the form's display name,
 * and the copies drifted. EditStudentForm's copy decided whether a form could be ADDED by testing
 * the instance name and the master form name concatenated together, for the literal string
 * "new student form 2025". Van Buren renamed their instance to "New Student Form 2026-2027" but
 * the master form is still called "New Student Form 2025" - so the test kept matching, and the
 * New Student Form was silently unofferable. 29 children ended up with every side form and no
 * New Student Form, which is the one that actually collects their information.
 *
 * So identity comes from form_type first (a slug the district cannot edit), and the name rules
 * survive only as the fallback for a row whose type is not set or not recognised.
 */

export const FORM_TYPES = {
  NEW_STUDENT: 'new_student',
  EMERGENCY_HEALTH: 'emergency_health',
  CHROMEBOOK: 'chromebook',
  EARLY_DISMISSAL: 'early_dismissal',
  RESIDENCY: 'residency',
  ACCEPTABLE_USE: 'acceptable_use',
  SCHOOL_LUNCH: 'school_lunch',
  HELPER: 'helper',
  RETURNING: 'returning'
};

const ALL_FORM_TYPES = Object.values(FORM_TYPES);

/** Household-level or dead: never owed by an individual child. */
const NOT_PER_STUDENT_TYPES = [FORM_TYPES.SCHOOL_LUNCH, FORM_TYPES.HELPER, FORM_TYPES.RETURNING];

/** An unrecognised type falls back to the name rules rather than being answered from the type. */
function knownType(formType) {
  return !!formType && ALL_FORM_TYPES.includes(formType);
}

/** Both names together, because a district renames the instance and the master keeps a stale one. */
function bothNames(instance) {
  const a = String((instance && instance.instance_name) || '').toLowerCase();
  const b = String((instance && instance.master_form_name) || '').toLowerCase();
  return `${a} ${b}`;
}

/** Is this form owed by an individual child (as opposed to the household)? */
export function isPerStudentForm(instance) {
  const t = instance && instance.form_type;
  if (knownType(t)) return !NOT_PER_STUDENT_TYPES.includes(t);
  const name = bothNames(instance);
  if (!name.trim()) return false;
  return !['returning', 'lunch', 'helper'].some((frag) => name.includes(frag));
}

/**
 * Is this form expected from a child in this grade?
 *
 * Grade bands are the district's policy, confirmed correct: Chromebook from Kindergarten (not
 * Preschool, not a blank grade); Early Dismissal through grade 6, and shown when the grade is
 * blank or unparseable. The asymmetry on a blank grade is deliberate.
 *
 * Chromebook was grade 1 and up until 2026-09-30. Keep this in step with
 * backend/services/formApplicability.js and the copy in NewStudentForm.js.
 */
export function formAppliesToGrade(instance, grade) {
  if (!isPerStudentForm(instance)) return false;

  const t = instance && instance.form_type;
  const typed = knownType(t);
  const name = bothNames(instance);
  const isChromebook = typed ? t === FORM_TYPES.CHROMEBOOK : name.includes('chromebook');
  const isDismissal = typed ? t === FORM_TYPES.EARLY_DISMISSAL : name.includes('dismissal');

  const g = grade == null ? '' : String(grade).trim();

  if (isChromebook) {
    if (!g || g === 'Preschool') return false;
    return true;
  }

  if (isDismissal) {
    if (!g) return true;
    const numeric = parseInt(g, 10);
    if (Number.isNaN(numeric)) return true;
    return !(numeric >= 7 && numeric <= 12);
  }

  return true;
}

const formApplicability = { FORM_TYPES, isPerStudentForm, formAppliesToGrade };
export default formApplicability;
